import React, { useId, useState } from 'react';

export function Section({ id, title, children }) {
  return <section className="api-section" id={id}><h2><a href={`#${id}`}>{title}<span aria-hidden="true"> #</span></a></h2>{children}</section>;
}

export function CodeBlock({ code, label = 'Code' }) {
  const [status, setStatus] = useState('');
  async function copy() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(code);
      setStatus('Copied');
    } catch {
      setStatus('Copy unavailable. Select the code to copy it manually.');
    }
  }
  return <div className="api-code"><div className="api-code__bar"><span>{label}</span><button type="button" onClick={copy} aria-label={`Copy ${label}`}>Copy</button></div><pre tabIndex="0" aria-label={label}><code>{code}</code></pre><span className="api-code__status" role="status">{status}</span></div>;
}

export function CodeTabs({ examples, label = 'Request examples' }) {
  const [active, setActive] = useState(0);
  const id = useId();
  const index = active < examples.length ? active : 0;
  return <div className="api-examples"><div className="api-tabs" role="tablist" aria-label={label}>{examples.map((example, i) => <button key={example.label} type="button" role="tab" id={`${id}-tab-${i}`} aria-controls={`${id}-panel-${i}`} aria-selected={index === i} tabIndex={index === i ? 0 : -1} onClick={() => setActive(i)} onKeyDown={(e) => {
    let next;
    if (e.key === 'ArrowRight') next = (i + 1) % examples.length;
    if (e.key === 'ArrowLeft') next = (i + examples.length - 1) % examples.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = examples.length - 1;
    if (next !== undefined) { e.preventDefault(); setActive(next); document.getElementById(`${id}-tab-${next}`)?.focus(); }
  }}>{example.label}</button>)}</div><div role="tabpanel" id={`${id}-panel-${index}`} aria-labelledby={`${id}-tab-${index}`}><CodeBlock key={examples[index].label} {...examples[index]} /></div></div>;
}

export function FieldTable({ rows, caption = 'Fields' }) {
  return <div className="api-table-wrap" tabIndex="0" role="region" aria-label={caption}><table className="api-table"><caption>{caption}</caption><thead><tr><th>Field</th><th>Type</th><th>Details</th></tr></thead><tbody>{rows.map(([name, type, detail], index) => <tr key={`${name}-${index}`}><td><code>{name}</code></td><td>{type}</td><td>{detail}</td></tr>)}</tbody></table></div>;
}

export function Notice({ children }) {
  return <aside className="api-notice">{children}</aside>;
}

export function BadgePreview() {
  return <figure className="api-preview"><div className="api-preview__stage"><div className="cs-entry-badge" role="img" aria-label="9,000 entries, 300X multiplier"><span className="cs-entry-badge__entries" aria-hidden="true">9,000 <small>Entries</small></span><span className="cs-entry-badge__multiplier" aria-hidden="true">300X</span></div></div></figure>;
}

// Source documents are rendered as text and code, never as executable HTML.
export function SourceText({ text }) {
  const lines = text.replace(/\r/g, '').split('\n');
  const blocks = [];
  let i = 0;
  const startsCode = (line) => /^(?:query |mutation |async function |function |const |let |var |document\.addEventListener|curl\b|<script>|\{|\[|roundFn =|roundedSubtotal =|effectiveEntryPer =|baseEntries =|finalEntries =|displayMultiplier =)/.test(line.trim());
  while (i < lines.length) {
    if (!lines[i].trim()) { i++; continue; }
    if (startsCode(lines[i])) {
      const code = [];
      const script = lines[i].trim() === '<script>';
      const formula = /^(roundFn|roundedSubtotal|effectiveEntryPer|baseEntries|finalEntries|displayMultiplier)\s*=/.test(lines[i].trim());
      let balance = 0;
      let ticks = false;
      do {
        const line = lines[i++];
        code.push(line);
        balance += Array.from(line).filter(char => '{[('.includes(char)).length - Array.from(line).filter(char => '}])'.includes(char)).length;
        if ((line.match(/(?<!\\)`/g) || []).length % 2) ticks = !ticks;
        if (formula ? !lines[i]?.trim() : script ? line.includes('</script>') : balance <= 0 && !ticks && !line.trimEnd().endsWith('\\') && !line.trimEnd().endsWith('=')) break;
      } while (i < lines.length);
      blocks.push(<CodeBlock key={blocks.length} label="Example" code={code.join('\n')} />);
    } else if (/^\s*(\*|\d+\.)\s/.test(lines[i])) {
      const items = [];
      while (i < lines.length && /^\s*(\*|\d+\.)\s/.test(lines[i])) items.push(lines[i++].trim().replace(/^(\*|\d+\.)\s*/, ''));
      blocks.push(<ul key={blocks.length}>{items.map((item, j) => <li key={j}>{item}</li>)}</ul>);
    } else {
      const para = [];
      while (i < lines.length && lines[i].trim() && !startsCode(lines[i]) && !/^\s*(\*|\d+\.)\s/.test(lines[i])) para.push(lines[i++]);
      blocks.push(<p className="api-source-text" key={blocks.length}>{para.join('\n')}</p>);
    }
  }
  return <>{blocks}</>;
}
